import React, { useState, ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../types/admin';
import AdminSidebar from '../components/AdminSidebar';
import AdminServiceSelectionModal from '../components/AdminServiceSelectionModal';
import CompanySelector from '../components/CompanySelector';
import DashboardSearchBar from '../../features/dashboard/components/DashboardSearchBar';
import AdminDashboard from '../../features/dashboard/pages/AdminDashboard';
import AdminOrdersList from '../../features/orders/pages/AdminOrdersList';
import AdminAccountsList from '../../features/team/pages/AdminAccountsList';
import AdminInvoicesList from '../../features/orders/pages/AdminInvoicesList';
import AdminUsersList from '../../features/team/pages/AdminUsersList';
import AdminFilingQueue from '../../features/admin/queue/AdminFilingQueue';
import OperatorMyWork from '../../features/work/pages/OperatorMyWork';
import OperatorTasks from '../../features/work/pages/OperatorTasks';
import AdminTeamManagement from '../../features/team/pages/AdminTeamManagement';
import AdminPerformanceDashboard from '../../features/dashboard/pages/AdminPerformanceDashboard';
import Leaderboard from '../../features/dashboard/pages/Leaderboard';
import AdminEFilingPage from '../../features/admin/efiling/AdminEFilingPage';
import AdminInviteForm from '../../features/admin/invitations/AdminInviteForm';
import OrderDetailPage from '../../features/orders/pages/OrderDetailPage';
import { useIsMobile } from '../hooks/useIsMobile';
import { withFreshStartSignal } from '../../features/efiling/lib/resolve-entry-intent';
import { MAIN_CONTENT_ID } from '../components/SkipLink';

type PlatformRole = 'admin' | 'operator';

// A trailing /admin/orders/<id> segment is an order id (UUID), not an orders sub-section.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface User {
  name?: string;
  email?: string;
}

interface AdminLayoutProps {
  brand: BrandConfig;
  Icons: IconsType;
  user?: User;
  onLogout: () => void;
  platformRole?: PlatformRole;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ brand, Icons, user, onLogout, platformRole = 'admin' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive current module from URL path
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentModule = pathParts[1] || 'dashboard'; // /admin/users -> 'users'
  const currentSubSection = pathParts[2] || getDefaultSubSection(currentModule);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const isMobile = useIsMobile();

  // Mock badge counts for sidebar (will be replaced with real data later)
  const [badgeCounts] = useState({
    queue: 12,
    myWork: 3,
    tasks: 5,
  });

  function getDefaultSubSection(moduleId: string): string {
    switch (moduleId) {
      case 'orders': return 'incoming';
      case 'users': return 'pending';
      case 'setup': return 'accounts';
      case 'finance': return 'invoices';
      case 'reports': return 'orders';
      default: return '';
    }
  }

  const handleModuleChange = (moduleId: string) => {
    const defaultSubSection = getDefaultSubSection(moduleId);
    if (defaultSubSection) {
      navigate(`/admin/${moduleId}/${defaultSubSection}`);
    } else {
      navigate(`/admin/${moduleId}`);
    }
  };

  const handleNavigate = (moduleId: string, subSection?: string) => {
    if (subSection) {
      navigate(`/admin/${moduleId}/${subSection}`);
    } else {
      navigate(`/admin/${moduleId}`);
    }
  };

  const handleSubSectionChange = (subSection: string) => {
    navigate(`/admin/${currentModule}/${subSection}`);
  };

  const handleNewOrder = () => {
    setIsServiceModalOpen(true);
  };

  const handleServiceSelect = (serviceId: string) => {
    setIsServiceModalOpen(false);
    if (serviceId === 'e-filing') {
      // Fresh-start launch site (admin): carry the fresh-start signal (and no
      // ?orderId=) so the shared wizard resolves on entry as a fresh start (R6).
      // The mount reads the query, not the path, so /admin/efiling?freshStart=1
      // resolves identically to the customer /eFiling?freshStart=1.
      navigate(withFreshStartSignal('/admin/efiling'));
    } else {
      navigate(`/orders/new/${serviceId}`);
    }
  };

  // The submitted search activates on the admin orders list through URL query
  // parameters: the bar lives in this layout's header and the list is a route
  // child, so the URL is the one channel they share — and it survives a
  // reload. One generic forward carries every offered criterion (the payload's
  // field and value, never a per-criterion branch), and a submission from an
  // order-detail route lands on the list, whose search it activates.
  const handleSearch = ({ field, value }: { field: string; value: string }) => {
    const params = new URLSearchParams({ search_field: field, search: value });
    const subSection =
      currentModule === 'orders' && !UUID_RE.test(currentSubSection)
        ? currentSubSection
        : getDefaultSubSection('orders');
    navigate(`/admin/orders/${subSection}?${params.toString()}`);
  };

  const renderContent = (): ReactElement => {
    switch (currentModule) {
      case 'dashboard':
        return (
          <AdminDashboard
            brand={brand}
            Icons={Icons}
            user={user}
            onNavigate={handleNavigate}
            platformRole={platformRole}
            onPlaceOrder={handleNewOrder}
          />
        );
      case 'efiling':
        return <AdminEFilingPage brand={brand} />;
      case 'queue':
        return (
          <AdminFilingQueue
            brand={brand}
            Icons={Icons}
            onNavigate={handleNavigate}
          />
        );
      case 'my-work':
        return (
          <OperatorMyWork
            brand={brand}
            Icons={Icons}
            onNavigate={handleNavigate}
          />
        );
      case 'tasks':
        return (
          <OperatorTasks
            brand={brand}
            Icons={Icons}
            onNavigate={handleNavigate}
            platformRole={platformRole}
          />
        );
      case 'team':
        // Admin only - redirect operators to dashboard
        if (platformRole !== 'admin') {
          return (
            <AdminDashboard
              brand={brand}
              Icons={Icons}
              user={user}
              onNavigate={handleNavigate}
              platformRole={platformRole}
              onPlaceOrder={handleNewOrder}
            />
          );
        }
        return (
          <AdminTeamManagement
            brand={brand}
            Icons={Icons}
            onNavigate={handleNavigate}
          />
        );
      case 'invitations':
        // Admin only - redirect operators to dashboard
        if (platformRole !== 'admin') {
          return (
            <AdminDashboard
              brand={brand}
              Icons={Icons}
              user={user}
              onNavigate={handleNavigate}
              platformRole={platformRole}
              onPlaceOrder={handleNewOrder}
            />
          );
        }
        return <AdminInviteForm brand={brand} />;
      case 'performance':
        // Admin only - redirect operators to dashboard
        if (platformRole !== 'admin') {
          return (
            <AdminDashboard
              brand={brand}
              Icons={Icons}
              user={user}
              onNavigate={handleNavigate}
              platformRole={platformRole}
              onPlaceOrder={handleNewOrder}
            />
          );
        }
        return (
          <AdminPerformanceDashboard
            brand={brand}
            Icons={Icons}
            onNavigate={handleNavigate}
          />
        );
      case 'leaderboard':
        // Visible to both operators and admins
        return (
          <Leaderboard
            brand={brand}
            Icons={Icons}
            onNavigate={handleNavigate}
          />
        );
      case 'orders':
        // /admin/orders/<uuid> → order detail; /admin/orders/<sub-section> → list
        if (UUID_RE.test(currentSubSection)) {
          return <OrderDetailPage orderId={currentSubSection} />;
        }
        return (
          <AdminOrdersList
            activeSubSection={currentSubSection}
            onSubSectionChange={handleSubSectionChange}
            brand={brand}
            Icons={Icons}
          />
        );
      case 'users':
        return (
          <AdminUsersList
            activeSubSection={currentSubSection}
            onSubSectionChange={handleSubSectionChange}
            brand={brand}
            Icons={Icons}
          />
        );
      case 'setup':
        return (
          <AdminAccountsList
            activeSubSection={currentSubSection}
            onSubSectionChange={handleSubSectionChange}
            brand={brand}
            Icons={Icons}
          />
        );
      case 'finance':
        return (
          <AdminInvoicesList
            activeSubSection={currentSubSection}
            onSubSectionChange={handleSubSectionChange}
            brand={brand}
            Icons={Icons}
          />
        );
      case 'reports':
        return (
          <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: brand.colors.bgLight }}>
            <div className="text-center">
              <Icons.Chart className="w-16 h-16 mx-auto mb-4" style={{ color: brand.colors.textMuted }} />
              <h2 className="text-xl font-semibold mb-2" style={{ color: brand.colors.secondary }}>
                Reports
              </h2>
              <p style={{ color: brand.colors.textLight }}>
                Reports module coming soon
              </p>
            </div>
          </div>
        );
      default:
        return (
          <AdminDashboard
            brand={brand}
            Icons={Icons}
            user={user}
            onNavigate={handleNavigate}
            onPlaceOrder={handleNewOrder}
          />
        );
    }
  };

  // Derive effective mobile menu state — auto-close when viewport widens to desktop
  const effectiveMobileMenuOpen = isMobile && isMobileMenuOpen;

  return (
      <div className="h-screen flex overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
        {/* Sidebar */}
        <AdminSidebar
          currentModule={currentModule}
          onModuleChange={handleModuleChange}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          isMobileOpen={effectiveMobileMenuOpen}
          onMobileToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          brand={brand}
          Icons={Icons}
          user={user}
          onLogout={onLogout}
          platformRole={platformRole}
          badgeCounts={badgeCounts}
          onNewOrder={handleNewOrder}
        />

        {/* Service Selection Modal */}
        <AdminServiceSelectionModal
          isOpen={isServiceModalOpen}
          onClose={() => setIsServiceModalOpen(false)}
          onSelectService={handleServiceSelect}
          brand={brand}
          Icons={Icons}
        />

        {/* Main Content - no margin on mobile, responsive on desktop */}
        <main
          id={MAIN_CONTENT_ID}
          tabIndex={-1}
          className="flex-1 flex flex-col transition-all duration-300 w-full lg:w-auto"
          style={{
            marginLeft: isMobile ? 0 : (isSidebarCollapsed ? '80px' : '256px'),
          }}
        >
          {/* Top Bar */}
          <header
            className="px-4 sm:px-6 py-3 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0"
            style={{ backgroundColor: 'white', borderColor: brand.colors.border }}
          >
            {/* Left: Greeting */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Mobile hamburger spacer */}
              <div className="w-8 lg:hidden" />
              {/* Chrome, not document structure: the page's own h1 must be the
                  first heading a screen-reader user meets on every admin route. */}
              <p
                className="text-lg sm:text-xl font-semibold whitespace-nowrap"
                style={{ color: brand.colors.secondary, fontFamily: brand.typography.fontFamily.heading }}
              >
                Hi, {user?.name || 'Administrator'}!
              </p>
            </div>

            {/* Right: Company Selector + Search */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <CompanySelector brand={brand} Icons={Icons} />
              <DashboardSearchBar brand={brand} Icons={Icons} onSearch={handleSearch} />
            </div>
          </header>

          {/* Page Content */}
          {renderContent()}
        </main>
      </div>
  );
};

export default AdminLayout;
